import { useEffect, useRef, useState } from 'react';
import { useOperator } from '../../contexts/OperatorContext';
import { BASE_URL_MAP } from '../../lib/environment';
import { sanitizeValue } from '../../lib/sanitize';
import {
  ApiSupportTicket,
  ApiSupportTicketUpdateAttachmentRequest,
} from '../../types/apiTypes';
import { SupportCreateTicket, SupportTicket } from '../../types/commonTypes';
import { FetchError, useFetch } from '../useFetch';
import { useSupportTicketCategories } from './useSupportTicketCategories';
import {
  hasSupportId,
  mapNewTicket,
  mapTicket,
  ticketListQuery,
} from './supportTicketModel';

export const useSupportTicket = (getCategories = true) => {
  const categories = useSupportTicketCategories(getCategories);
  const action = useRef<
    'getTickets' | 'updateTicket' | 'createTicket' | 'getTicket' | null
  >(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [validationError, setValidationError] = useState<FetchError | null>(
    null
  );
  const { error, loading, makeRequest, response, isSuccess } = useFetch();
  const operator = useOperator();
  const BASE_URL = `${BASE_URL_MAP[operator.type]}/${
    operator.id
  }/SupportTicket`;

  useEffect(() => {
    if (!loading && response && isSuccess) {
      if (action.current === 'getTickets') {
        setTickets(
          (response.Tickets ?? []).flatMap((ticket: ApiSupportTicket) => {
            const mapped = mapTicket(ticket);
            return mapped ? [mapped] : [];
          })
        );
      } else if (action.current === 'updateTicket') {
        const ticket = mapTicket(response);
        if (ticket)
          setTickets((previous) => [
            ticket,
            ...previous.filter((item) => item.id !== ticket.id),
          ]);
      }
      action.current = null;
    }
  }, [loading, response, isSuccess]);

  const failValidation = (message: string) => {
    setValidationError({
      data: message,
      status: 400,
      statusText: 'Invalid request',
    });
  };

  const addMessage = (
    id: string,
    message: string,
    ccList: string,
    attachment?: ApiSupportTicketUpdateAttachmentRequest
  ) => {
    setValidationError(null);
    if (!hasSupportId(id)) {
      failValidation('This ticket has no valid ID. Refresh the ticket list.');
      return;
    }
    action.current = 'updateTicket';
    makeRequest(`${BASE_URL}/${encodeURIComponent(id)}`, 'put', {
      Attachment: attachment ?? null,
      Cc: sanitizeValue(ccList),
      Text: sanitizeValue(message),
    });
  };

  const createTicket = (ticket: SupportCreateTicket) => {
    setValidationError(null);
    try {
      if (!categories.some((category) => category.id === ticket.category)) {
        throw new Error('Select a category from the available options.');
      }
      const body = mapNewTicket(ticket);
      action.current = 'createTicket';
      const siteId = ticket.site;
      const query =
        operator.type === 'Hospitality' &&
        siteId &&
        siteId.toLowerCase() !== 'all'
          ? `?${new URLSearchParams({ accountSiteId: siteId })}`
          : '';
      makeRequest(`${BASE_URL}${query}`, 'post', body);
    } catch (error) {
      failValidation(
        error instanceof Error ? error.message : 'Invalid ticket.'
      );
    }
  };

  const getTickets = (
    pending: boolean,
    startDate: Date,
    endDate: Date,
    siteId?: string | null,
    page?: string
  ) => {
    setValidationError(null);
    action.current = 'getTickets';
    makeRequest(
      `${BASE_URL}?${ticketListQuery(
        operator.type,
        pending,
        startDate,
        endDate,
        siteId,
        page
      )}`,
      'get'
    );
  };

  const getTicket = (id: string) => {
    setValidationError(null);
    if (!hasSupportId(id)) {
      failValidation('This ticket has no valid ID. Refresh the ticket list.');
      return;
    }
    action.current = 'getTicket';
    makeRequest(
      `${BASE_URL}/${encodeURIComponent(id)}`,
      'get',
      undefined,
      'json',
      'no-store'
    );
  };

  return {
    addMessage,
    categories,
    createTicket,
    error: validationError ?? error,
    getTicket,
    getTickets,
    loading,
    response,
    tickets,
    isSuccess,
  };
};
